import React from 'react';
import _ from 'underscore';

import ModelTypeConstants from 'constants/ModelTypeConstants';
import * as SourceConstants from 'constants/SourceConstants';
import {NavStoreProject} from 'constants/SourceConstants';
import {Party} from 'parties/PartyConstants';
import preferences from 'Preferences';
import {SortKey} from 'search/SearchConstants';
import APIUtil from 'util/APIUtil';
import sortedBy from 'util/sortedBy';

import {ProjectPreferences, Section} from './ProjectListingConstants';

export const SHARED_WITH_ME = 'SHARED_WITH_ME';
export const MY_PROJECTS = 'MY_PROJECTS';
export const STARRED = 'STARRED';

const ALL_SECTION = {
  collapsed: false,
  id: SourceConstants.ALL,
  name: 'All',
  showTitle: false,
  renderEmptySection: () => <span>No projects yet.</span>,
};

const HIDDEN_SECTION = {
  collapsed: true,
  id: SourceConstants.HIDDEN,
  name: 'Hidden',
  showTitle: true,
};

const ARCHIVED_SECTION = {
  collapsed: true,
  id: SourceConstants.ARCHIVED,
  name: 'Archived',
  showTitle: true,
};

// Returns an array of sections to be displayed.
export function getSections(): Array<Section> {
  const sections = [ALL_SECTION, HIDDEN_SECTION, ARCHIVED_SECTION];

  // Restore collapsed/expanded state of sections from preferences
  return sections.map((section) => {
    const collapsedPreference = preferences.get(`${ProjectPreferences.SECTION_COLLAPSED}.${section.id}`);
    return {
      ...section,
      collapsed: collapsedPreference == null ? section.collapsed : collapsedPreference,
    };
  });
}

export function isSharedWithOrg(project: NavStoreProject, organizationId: string, userId: string): boolean {
  const organizationIsOwner = project.s_owner.id === organizationId;
  // eslint-disable-next-line no-restricted-syntax
  const currentUserIsCollaborator = _.some(
    project.collaborators,
    (collaborator: Party) => collaborator.id === userId
  );

  // If project is owned by org, but has no member access with the current user
  // as a collaborator, consider it a Personal project.
  const isPersonalProject =
    organizationIsOwner && project.ownerInfo.memberDataPolicyId == null && currentUserIsCollaborator;

  // eslint-disable-next-line no-restricted-syntax
  const organizationIsCollaborator = _.some(
    project.collaborators,
    (collaborator: Party) => collaborator.id === organizationId
  );

  return (organizationIsOwner && !isPersonalProject) || organizationIsCollaborator;
}

export function isSharedWithTeam(project: NavStoreProject, teamId: string): boolean {
  // eslint-disable-next-line no-restricted-syntax
  return _.some(project.collaborators, (collaborator: Party) => teamId === collaborator.id);
}

export function isOwnedByUser(project: NavStoreProject, userId: string): boolean {
  return project.s_owner.id === userId;
}

export function isSharedWithUser(project: NavStoreProject, userId: string): boolean {
  return (
    !isOwnedByUser(project, userId) &&
    // eslint-disable-next-line no-restricted-syntax
    _.some(project.collaborators, (collaborator: Party) => collaborator.id === userId)
  );
}

export function filterProjectsByCollaboratorId(
  projects: Array<NavStoreProject>,
  collaboratorId: string,
  userId: string
): Array<NavStoreProject> {
  if (APIUtil.apiIdToType(collaboratorId) === ModelTypeConstants.TEAM) {
    return projects.filter((project) => isSharedWithTeam(project, collaboratorId));
  }
  if (APIUtil.apiIdToType(collaboratorId) === ModelTypeConstants.ENTITY) {
    return projects.filter((project) => isSharedWithOrg(project, collaboratorId, userId));
  }
  return projects;
}

// Return object mapping from `section` to a list of `sources` that correspond to that section
// Note that a project can be in multiple sections (e.g. if it's shared with a team and an organization)
export function getGroupedSources(
  sections: Array<Section>,
  sources: Array<NavStoreProject>,
  sortKey?: string,
  sortReverse?: boolean
): {
  [sectionId: string]: Array<NavStoreProject>;
} {
  const groupedProjects: {[sectionId: string]: Array<NavStoreProject>} = {};
  sections.forEach((section) => {
    groupedProjects[section.id] = [];
  });

  sources.forEach((source: NavStoreProject) => {
    // TODO(neena): `source.archiveRecord` is the new method for archiving projects. The old
    // `source.archived` implementation will be phased away in favor of the new system.
    if (source.archiveRecord) {
      groupedProjects[SourceConstants.ARCHIVED].push(source);
      return;
    }
    // TODO(neena): Remove `source.archived` once existing projects are migrated to the new archive system.
    if (source.archived || source.hidden) {
      groupedProjects[SourceConstants.HIDDEN].push(source);
      return;
    }
    groupedProjects[SourceConstants.ALL].push(source);
  });
  const {accessor, naturalSort} = getSortParams(sortKey ?? '');
  sections.forEach((section: Section) => {
    const projects = groupedProjects[section.id] || [];
    groupedProjects[section.id] = sortedBy<NavStoreProject>(projects, [
      {accessor: (source) => String(source.starred), reversed: true},
      {accessor, naturalSort, reversed: sortReverse},
    ]);
  });
  return groupedProjects;
}

function getSortParams(sortKey: string): {
  accessor: keyof SourceConstants.NavStoreProject;
  naturalSort: boolean;
} {
  switch (sortKey) {
    case SortKey.SORT_CREATED_AT_KEY:
      return {accessor: 'createdAt', naturalSort: false};
    default:
      return {accessor: 'name', naturalSort: true};
  }
}
